import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, UsersIcon, FileTextIcon, TrendingUpIcon, MessageSquareIcon, ChevronRightIcon, ShareIcon, SettingsIcon, DatabaseIcon, ShoppingCartIcon, ExternalLinkIcon, SparklesIcon, AlertCircleIcon, CheckCircle2Icon, ArrowUpIcon, BriefcaseIcon, MailIcon, PhoneIcon, MapPinIcon } from 'lucide-react';
import { Account } from '../types';
import { LiveChatPreview } from './LiveChatPreview';
import { AnatomyPanel } from './AnatomyPanel';
import { ShareDrawer } from './ShareDrawer';
import { useUser } from '../lib/userContext';
import { ActivityFeed } from './ActivityFeed';
import { AccountSummaryChips } from './AccountSummaryChips';
import { useActivityFeed } from '../lib/activityFeed';
interface PartnerViewProps {
  account: Account;
  onClose: () => void;
  initialBotType?: 'internal' | 'external';
}
export function PartnerView({
  account,
  onClose,
  initialBotType
}: PartnerViewProps) {
  const {
    hasPermission
  } = useUser();
  const {
    addActivity,
    getActivities
  } = useActivityFeed();
  const [activeChatBot, setActiveChatBot] = useState<'internal' | 'external'>(initialBotType || 'external');
  const [showAnatomyPanel, setShowAnatomyPanel] = useState(false);
  const [showShareDrawer, setShowShareDrawer] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const externalBot = account.bots.find(b => b.type === 'external');
  const internalBot = account.bots.find(b => b.type === 'internal');
  const activities = getActivities(account.id);
  // Log activity when partner view opens
  useEffect(() => {
    addActivity(account.id, {
      type: 'system',
      message: `Opened Partner View for ${account.name}`
    });
  }, [account.id, account.name, addActivity]);
  const handleNavigate = (view: string, metadata?: any) => {
    console.log('Navigate to:', view, metadata);
  };
  const handleAction = (action: string, metadata?: any) => {
    console.log('Action:', action, metadata);
  };
  const handleChatBotSwitch = (botType: 'internal' | 'external') => {
    setActiveChatBot(botType);
    const botName = botType === 'external' ? externalBot?.name : internalBot?.name;
    addActivity(account.id, {
      type: 'chat',
      message: `Switched to ${botName} chatbot`
    });
  };
  const handleQuickLink = (view: string) => {
    const event = new CustomEvent('quad-navigate', {
      detail: {
        view,
        metadata: {
          accountId: account.id
        }
      }
    });
    window.dispatchEvent(event);
    addActivity(account.id, {
      type: 'system',
      message: `Navigated to ${view} view`
    });
    onClose();
  };
  const healthColor = account.health === 'Green' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30 shadow-emerald-500/20' : account.health === 'Yellow' ? 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30 shadow-yellow-500/20' : 'text-red-400 bg-red-500/10 border-red-500/30 shadow-red-500/20';
  return <>
      {/* Backdrop */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-968" />
      {/* Modal Container - Expanded to use more screen space */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-969">
        <div className="relative w-[95vw] h-[92vh] max-w-[1920px] pointer-events-auto flex flex-col" data-id="element-970">
          {/* Modal Content with proper scroll container */}
          <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border border-slate-800 rounded-2xl shadow-2xl flex flex-col h-full overflow-hidden" data-id="element-971">
            {/* Header - Fixed at top */}
            <div className="flex-shrink-0 px-6 py-4 border-b border-slate-800/80 bg-slate-950/50" data-id="element-972">
              {/* Decorative gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" data-id="element-973" />
              <div className="relative flex items-center justify-between" data-id="element-974">
                <div className="relative flex items-center gap-6 flex-1" data-id="element-975">
                  <div className="w-24 h-12 rounded-xl bg-white/95 p-2.5 flex items-center justify-center shadow-2xl ring-2 ring-slate-700/30 hover:ring-blue-500/40 transition-all duration-300 hover:scale-105" data-id="element-976">
                    <img src={account.logo_url} alt={account.name} className="w-full h-full object-contain" data-id="element-977" />
                  </div>
                  <div data-id="element-978">
                    <div className="flex items-center gap-4 mb-2" data-id="element-979">
                      <h2 className="text-3xl font-bold text-white tracking-tight bg-gradient-to-r from-white via-blue-50 to-slate-200 bg-clip-text text-transparent" data-id="element-980">
                        {account.name}
                      </h2>
                      <span className={`px-4 py-1.5 rounded-xl text-xs font-bold border shadow-lg ${healthColor}`} data-id="element-981">
                        {account.health}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-400" data-id="element-982">
                      <span className="font-medium" data-id="element-983">{account.industry}</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-600" data-id="element-984" />
                      <span className="font-medium" data-id="element-985">{account.region}</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-600" data-id="element-986" />
                      <span className="flex items-center gap-1.5 font-medium" data-id="element-987">
                        <SparklesIcon className="w-4 h-4 text-amber-400" data-id="element-988" />
                        {account.products_services.length} Products
                      </span>
                    </div>
                  </div>
                </div>
                <div className="relative flex items-center gap-3" data-id="element-989">
                  <button onClick={() => setShowShareDrawer(true)} className="px-6 py-3 bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/50 hover:border-slate-600 text-white text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2.5 shadow-xl shadow-black/30 hover:shadow-2xl hover:shadow-black/40 hover:scale-105" data-id="element-990">
                    <ShareIcon className="w-4 h-4" data-id="element-991" />
                    Share
                  </button>
                  <button onClick={() => setShowAnatomyPanel(!showAnatomyPanel)} className="px-6 py-3 bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/50 hover:border-slate-600 text-white text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2.5 shadow-xl shadow-black/30 hover:shadow-2xl hover:shadow-black/40 hover:scale-105" data-id="element-992">
                    <SettingsIcon className="w-4 h-4" data-id="element-993" />
                    Anatomy
                  </button>
                  <button onClick={onClose} className="p-3 hover:bg-slate-800/60 rounded-xl transition-all duration-200 text-slate-400 hover:text-white hover:scale-105" data-id="element-994">
                    <XIcon className="w-6 h-6" data-id="element-995" />
                  </button>
                </div>
              </div>
            </div>
            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden" data-id="element-996">
              {/* Content Grid - Increase gap slightly for better breathing room */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-7 p-8 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" data-id="element-997">
                {/* Left Column */}
                <div className="lg:col-span-2 space-y-5 overflow-y-auto pr-3 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-998">
                  {/* Enhanced KPIs */}
                  <div className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-999">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1000" />
                    <div className="relative" data-id="element-1001">
                      <div className="flex items-center gap-2.5 mb-5" data-id="element-1002">
                        <div className="p-2 rounded-lg bg-blue-500/10" data-id="element-1003">
                          <TrendingUpIcon className="w-5 h-5 text-blue-400" data-id="element-1004" />
                        </div>
                        <h3 className="text-base font-bold text-white uppercase tracking-wider" data-id="element-1005">
                          Key Metrics
                        </h3>
                      </div>
                      <div className="grid grid-cols-3 gap-4" data-id="element-1006">
                        <div className="group/kpi relative bg-slate-950/60 rounded-xl p-5 text-center border border-slate-800/60 hover:border-blue-500/40 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-blue-500/10 overflow-hidden" data-id="element-1007">
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-blue-500/5 opacity-0 group-hover/kpi:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-1008" />
                          <div className="relative" data-id="element-1009">
                            <div className="flex items-center justify-center mb-3" data-id="element-1010">
                              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover/kpi:bg-blue-500/20 transition-colors" data-id="element-1011">
                                <TrendingUpIcon className="w-5 h-5 text-blue-400" data-id="element-1012" />
                              </div>
                            </div>
                            <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider font-bold" data-id="element-1013">
                              Active Jobs
                            </p>
                            <p className="text-3xl font-bold text-white group-hover/kpi:scale-110 transition-transform" data-id="element-1014">
                              {account.kpis.active_jobs}
                            </p>
                          </div>
                        </div>
                        <div className="group/kpi relative bg-slate-950/60 rounded-xl p-5 text-center border border-slate-800/60 hover:border-green-500/40 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-green-500/10 overflow-hidden" data-id="element-1015">
                          <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-500/5 opacity-0 group-hover/kpi:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-1016" />
                          <div className="relative" data-id="element-1017">
                            <div className="flex items-center justify-center mb-3" data-id="element-1018">
                              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center group-hover/kpi:bg-green-500/20 transition-colors" data-id="element-1019">
                                <CheckCircle2Icon className="w-5 h-5 text-green-400" data-id="element-1020" />
                              </div>
                            </div>
                            <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider font-bold" data-id="element-1021">
                              Open Quotes
                            </p>
                            <p className="text-3xl font-bold text-white group-hover/kpi:scale-110 transition-transform" data-id="element-1022">
                              {account.kpis.open_quotes}
                            </p>
                          </div>
                        </div>
                        <div className="group/kpi relative bg-slate-950/60 rounded-xl p-5 text-center border border-slate-800/60 hover:border-red-500/40 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-red-500/10 overflow-hidden" data-id="element-1023">
                          <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-red-500/5 opacity-0 group-hover/kpi:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-1024" />
                          <div className="relative" data-id="element-1025">
                            <div className="flex items-center justify-center mb-3" data-id="element-1026">
                              <div className={`w-10 h-10 rounded-xl ${account.kpis.sla_alerts > 0 ? 'bg-red-500/10' : 'bg-slate-700/10'} flex items-center justify-center group-hover/kpi:bg-red-500/20 transition-colors`} data-id="element-1027">
                                <AlertCircleIcon className={`w-5 h-5 ${account.kpis.sla_alerts > 0 ? 'text-red-400' : 'text-slate-500'}`} data-id="element-1028" />
                              </div>
                            </div>
                            <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider font-bold" data-id="element-1029">
                              SLA Alerts
                            </p>
                            <p className={`text-3xl font-bold group-hover/kpi:scale-110 transition-transform ${account.kpis.sla_alerts > 0 ? 'text-red-400' : 'text-green-400'}`} data-id="element-1030">
                              {account.kpis.sla_alerts}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Enhanced Team */}
                  <div className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1031">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1032" />
                    <div className="relative" data-id="element-1033">
                      <div className="flex items-center gap-2.5 mb-5" data-id="element-1034">
                        <div className="p-2 rounded-lg bg-purple-500/10" data-id="element-1035">
                          <UsersIcon className="w-5 h-5 text-purple-400" data-id="element-1036" />
                        </div>
                        <h3 className="text-base font-bold text-white uppercase tracking-wider" data-id="element-1037">
                          Team Members
                        </h3>
                      </div>
                      <div className="space-y-2.5" data-id="element-1038">
                        {account.people.slice(0, 3).map(person => <div key={person.id} onClick={() => setSelectedPerson(person)} className="group/member flex items-center gap-4 p-4 hover:bg-slate-800/60 rounded-xl transition-all duration-200 cursor-pointer border border-transparent hover:border-slate-700/50 hover:shadow-lg hover:shadow-black/20" data-id="element-1039">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-base font-bold shadow-lg ring-2 ring-blue-500/20 group-hover/member:ring-blue-500/40 group-hover/member:scale-110 transition-all duration-300 flex-shrink-0" data-id="element-1040">
                              {person.name.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-1041">
                              <p className="text-sm font-semibold text-white truncate group-hover/member:text-blue-400 transition-colors" data-id="element-1042">
                                {person.name}
                              </p>
                              <p className="text-xs text-slate-400 truncate" data-id="element-1043">
                                {person.role}
                              </p>
                            </div>
                            <ChevronRightIcon className="w-5 h-5 text-slate-600 group-hover/member:text-slate-400 group-hover/member:translate-x-1 transition-all duration-200 flex-shrink-0" data-id="element-1044" />
                          </div>)}
                        {account.people.length > 3 && <button className="w-full text-xs text-blue-400 hover:text-blue-300 py-3.5 flex items-center justify-center gap-2 rounded-xl hover:bg-slate-800/40 transition-all duration-200 font-semibold border border-transparent hover:border-slate-700/50" data-id="element-1045">
                            View all {account.people.length} members
                            <ArrowUpIcon className="w-3.5 h-3.5 rotate-90" data-id="element-1046" />
                          </button>}
                      </div>
                    </div>
                  </div>
                  {/* Enhanced Products & Services */}
                  <div className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1047">
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1048" />
                    <div className="relative" data-id="element-1049">
                      <div className="flex items-center gap-2.5 mb-5" data-id="element-1050">
                        <div className="p-2 rounded-lg bg-amber-500/10" data-id="element-1051">
                          <FileTextIcon className="w-5 h-5 text-amber-400" data-id="element-1052" />
                        </div>
                        <h3 className="text-base font-bold text-white uppercase tracking-wider" data-id="element-1053">
                          Products & Services
                        </h3>
                      </div>
                      <div className="flex flex-wrap gap-2.5" data-id="element-1054">
                        {account.products_services.map(service => <span key={service} className="text-xs px-4 py-2 bg-slate-800/60 hover:bg-slate-700/60 text-slate-300 hover:text-white rounded-xl border border-slate-700/60 hover:border-slate-600 transition-all duration-200 shadow-sm hover:shadow-md font-medium hover:scale-105" data-id="element-1055">
                            {service}
                          </span>)}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Middle Column - Enhanced Live Chat */}
                <div className="lg:col-span-2 space-y-5" data-id="element-1056">
                  <div className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl overflow-hidden flex flex-col shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300" style={{
                  height: '600px'
                }} data-id="element-1057">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1058" />
                    {/* Enhanced Chat Header with Bot Switcher */}
                    <div className="relative flex items-center gap-3 p-5 border-b border-slate-800/80 bg-slate-950/80" data-id="element-1059">
                      {externalBot && <button onClick={() => handleChatBotSwitch('external')} className={`flex-1 px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${activeChatBot === 'external' ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-xl shadow-blue-600/40 ring-2 ring-blue-500/40 scale-[1.02]' : 'bg-slate-800/60 text-slate-400 hover:bg-slate-700/60 hover:text-white border border-slate-700/50 hover:border-slate-600 hover:scale-[1.02]'}`} data-id="element-1060">
                          <MessageSquareIcon className="w-4 h-4 inline mr-2" data-id="element-1061" />
                          {externalBot.name}
                        </button>}
                      {internalBot && hasPermission('view_internal_spaces') && <button onClick={() => handleChatBotSwitch('internal')} className={`flex-1 px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${activeChatBot === 'internal' ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-xl shadow-purple-600/40 ring-2 ring-purple-500/40 scale-[1.02]' : 'bg-slate-800/60 text-slate-400 hover:bg-slate-700/60 hover:text-white border border-slate-700/50 hover:border-slate-600 hover:scale-[1.02]'}`} data-id="element-1062">
                          <MessageSquareIcon className="w-4 h-4 inline mr-2" data-id="element-1063" />
                          QuadChat
                        </button>}
                    </div>
                    {/* Chat Content */}
                    <div className="relative flex-1 min-h-0" data-id="element-1064">
                      <LiveChatPreview account={account} botType={activeChatBot} section="overview" onNavigate={handleNavigate} onAction={handleAction} data-id="element-1065" />
                    </div>
                  </div>
                </div>
                {/* Right Column - Enhanced Activity Feed */}
                <div className="lg:col-span-1" data-id="element-1066">
                  <div className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl overflow-hidden flex flex-col shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300" style={{
                  height: '600px'
                }} data-id="element-1067">
                    <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1068" />
                    {/* Summary Chips */}
                    <div className="relative" data-id="element-1069">
                      <AccountSummaryChips account={account} activities={activities} onNavigate={handleNavigate} data-id="element-1070" />
                    </div>
                    {/* Activity Feed */}
                    <div className="relative flex-1 overflow-hidden" data-id="element-1071">
                      <ActivityFeed account={account} data-id="element-1072" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Enhanced Quick Links Footer - Restore original padding */}
              <div className="relative px-6 py-4 border-t border-slate-800/80 bg-slate-950/50" data-id="element-1073">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" data-id="element-1074" />
                <div className="relative flex items-center justify-between" data-id="element-1075">
                  <div className="flex items-center gap-2.5" data-id="element-1076">
                    <SparklesIcon className="w-4 h-4 text-amber-400" data-id="element-1077" />
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-1078">
                      Quick Actions
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1079">
                    <button onClick={() => handleQuickLink('people')} className="px-5 py-2.5 bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 text-white text-xs font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 hover:scale-105" data-id="element-1080">
                      <UsersIcon className="w-3.5 h-3.5" data-id="element-1081" />
                      View People
                      <ExternalLinkIcon className="w-2.5 h-2.5 opacity-50" data-id="element-1082" />
                    </button>
                    <button onClick={() => handleQuickLink('orders')} className="px-5 py-2.5 bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 text-white text-xs font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 hover:scale-105" data-id="element-1083">
                      <ShoppingCartIcon className="w-3.5 h-3.5" data-id="element-1084" />
                      View Orders
                      <ExternalLinkIcon className="w-2.5 h-2.5 opacity-50" data-id="element-1085" />
                    </button>
                    <button onClick={() => handleQuickLink('spaces')} className="px-5 py-2.5 bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 text-white text-xs font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 hover:scale-105" data-id="element-1086">
                      <DatabaseIcon className="w-3.5 h-3.5" data-id="element-1087" />
                      Knowledge Spaces
                      <ExternalLinkIcon className="w-2.5 h-2.5 opacity-50" data-id="element-1088" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      {/* Person Detail Modal */}
      <AnimatePresence data-id="element-1089">
        {selectedPerson && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setSelectedPerson(null)} className="fixed inset-0 bg-black/75 backdrop-blur-md z-[110]" data-id="element-1090" />
            <div className="fixed inset-0 z-[111] overflow-y-auto" data-id="element-1091">
              <div className="flex min-h-full items-center justify-center p-4" data-id="element-1092">
                <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} onClick={e => e.stopPropagation()} className="relative w-full max-w-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-slate-800/80 rounded-3xl shadow-2xl overflow-hidden" data-id="element-1093">
                  {/* Modal Header */}
                  <div className="relative px-8 py-6 border-b border-slate-800/80 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950" data-id="element-1094">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-transparent to-blue-500/5 pointer-events-none" data-id="element-1095" />
                    <div className="relative flex items-start justify-between" data-id="element-1096">
                      <div className="flex items-center gap-4" data-id="element-1097">
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-white text-2xl font-bold shadow-2xl ring-2 ring-purple-500/30" data-id="element-1098">
                          {selectedPerson.name.charAt(0)}
                        </div>
                        <div data-id="element-1099">
                          <h2 className="text-2xl font-bold text-white mb-1 bg-gradient-to-r from-white to-slate-200 bg-clip-text text-transparent" data-id="element-1100">
                            {selectedPerson.name}
                          </h2>
                          <p className="text-slate-400 flex items-center gap-2" data-id="element-1101">
                            <BriefcaseIcon className="w-4 h-4" data-id="element-1102" />
                            {selectedPerson.role}
                          </p>
                        </div>
                      </div>
                      <button onClick={() => setSelectedPerson(null)} className="p-2.5 hover:bg-slate-800/60 rounded-xl transition-all text-slate-400 hover:text-white hover:scale-105" data-id="element-1103">
                        <XIcon className="w-6 h-6" data-id="element-1104" />
                      </button>
                    </div>
                  </div>
                  {/* Modal Content */}
                  <div className="p-8 space-y-6 max-h-[calc(85vh-140px)] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-1105">
                    {/* Type Badge */}
                    <div className={`inline-flex px-4 py-2 rounded-xl text-sm font-bold border shadow-lg ${selectedPerson.organization === 'Client' ? 'bg-blue-500/10 border-blue-500/30 text-blue-400' : 'bg-purple-500/10 border-purple-500/30 text-purple-400'}`} data-id="element-1106">
                      {selectedPerson.organization === 'Client' ? 'Client Contact' : 'Quad Team Member'}
                    </div>
                    {/* Contact Information */}
                    <div className="space-y-4" data-id="element-1107">
                      <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider" data-id="element-1108">
                        Contact Information
                      </h3>
                      <div className="space-y-3" data-id="element-1109">
                        <div className="flex items-center gap-3 p-4 bg-slate-900/60 border border-slate-800/60 rounded-xl" data-id="element-1110">
                          <MailIcon className="w-5 h-5 text-blue-400 flex-shrink-0" data-id="element-1111" />
                          <div data-id="element-1112">
                            <p className="text-xs text-slate-500 mb-1" data-id="element-1113">Email</p>
                            <a href={`mailto:${selectedPerson.email}`} className="text-sm text-white hover:text-blue-400 transition-colors" data-id="element-1114">
                              {selectedPerson.email}
                            </a>
                          </div>
                        </div>
                        {selectedPerson.phone && <div className="flex items-center gap-3 p-4 bg-slate-900/60 border border-slate-800/60 rounded-xl" data-id="element-1115">
                            <PhoneIcon className="w-5 h-5 text-green-400 flex-shrink-0" data-id="element-1116" />
                            <div data-id="element-1117">
                              <p className="text-xs text-slate-500 mb-1" data-id="element-1118">
                                Phone
                              </p>
                              <a href={`tel:${selectedPerson.phone}`} className="text-sm text-white hover:text-green-400 transition-colors" data-id="element-1119">
                                {selectedPerson.phone}
                              </a>
                            </div>
                          </div>}
                        {selectedPerson.location && <div className="flex items-center gap-3 p-4 bg-slate-900/60 border border-slate-800/60 rounded-xl" data-id="element-1120">
                            <MapPinIcon className="w-5 h-5 text-purple-400 flex-shrink-0" data-id="element-1121" />
                            <div data-id="element-1122">
                              <p className="text-xs text-slate-500 mb-1" data-id="element-1123">
                                Location
                              </p>
                              <p className="text-sm text-white" data-id="element-1124">
                                {selectedPerson.location}
                              </p>
                            </div>
                          </div>}
                      </div>
                    </div>
                    {/* Account Information */}
                    <div className="space-y-4" data-id="element-1125">
                      <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider" data-id="element-1126">
                        Account
                      </h3>
                      <div className="flex items-center gap-4 p-5 bg-slate-900/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all" data-id="element-1127">
                        <div className="w-14 h-14 rounded-xl bg-white/95 p-2.5 flex items-center justify-center shadow-lg flex-shrink-0" data-id="element-1128">
                          <img src={account.logo_url} alt={account.name} className="w-full h-full object-contain" data-id="element-1129" />
                        </div>
                        <div data-id="element-1130">
                          <p className="text-sm font-semibold text-white mb-1" data-id="element-1131">
                            {account.name}
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-1132">
                            Client Account
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </>}
      </AnimatePresence>
      {/* Anatomy Panel */}
      <AnimatePresence data-id="element-1133">
        {showAnatomyPanel && <AnatomyPanel account={account} onClose={() => setShowAnatomyPanel(false)} data-id="element-1134" />}
      </AnimatePresence>
      {/* Share Drawer */}
      <AnimatePresence data-id="element-1135">
        {showShareDrawer && <ShareDrawer account={account} onClose={() => setShowShareDrawer(false)} data-id="element-1136" />}
      </AnimatePresence>
    </>;
}